import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PlanElement, PlanPlatz } from "@shared/plan";
import { api, rufe } from "../api/client";

export interface MieterStart {
  mieter: { id: string; name: string; mieternummer: string; status: string };
  laden: { name: string; slug: string; oeffnungszeiten: Record<string, { von: string; bis: string } | null> | null; logo?: boolean; adresse?: { strasse?: string; plz?: string; ort?: string } };
  guthabenCent: number;
  verkaeufeHeute: { anzahl: number; summeCent: number };
  letzteVerkaeufe: { id: string; code: string; bezeichnung: string; preisCent: number; statusAt: string; fotoKey: string | null }[];
  artikelImRegal: number;
  rueckgabeKiste: number;
  offeneVertraege: { id: string; nummer: string; regalNummer: string; start: string; ende: string | null; art: "fest" | "abo" }[];
  buchungen: { id: string; nummer: string; regalNummer: string; regalLage: string; art: "fest" | "abo"; start: string; ende: string | null; einheit: "woche" | "monat"; mieteCent: number; provisionProzent: number; kautionCent: number; gekuendigtZum: string | null; mietendeWahl: "verlaengern" | "abholen" | "spenden" | null; kaution: { betragCent: number; erhaltenAt: string | null } | null }[];
  hinweise: { id: string; art: "aufraeumen" | "rueckgabe_kiste" | "mietende" | "info"; text: string; erstelltAt: string; gelesenAt: string | null }[];
}

export interface MieterVertrag {
  id: string;
  nummer: string;
  status: "entwurf" | "reserviert" | "aktiv" | "beendet" | "storniert";
  art: "fest" | "abo";
  start: string;
  ende: string | null;
  einheit: "woche" | "monat";
  kuendigungsfristWochen: number;
  mieteCent: number;
  provisionProzent: number;
  kautionCent: number;
  zustimmungAt: string | null;
  zustimmungArt: "portal" | "vor_ort" | null;
  vertragVersion: string | null;
  vertragTextHash: string | null;
  text: string | null;
  regal: { nummer: string; lage: string; groesse: string } | undefined;
}

export interface MieterAbrechnungen {
  laufend: { von: string | null; umsatzCent: number; rabatteCent: number; rueckgabenCent: number; provisionCent: number; mieteCent: number; summeCent: number };
  abrechnungen: { id: string; nummer: string; art: "periode" | "schluss"; periodeVon: string; periodeBis: string; auszahlungCent: number; status: string; pdfKey: string | null; versendetAt: string | null }[];
}

export interface MieterProfil {
  id: string;
  name: string;
  mieternummer: string;
  email: string;
  telefon: string;
  adresse: { strasse?: string; plz?: string; ort?: string };
  gewerblich: boolean;
  ibanMaske: string | null;
  kontoinhaber: string;
  auszahlungsweg: "ueberweisung" | "bar";
}

export interface RueckgabeKisteDaten {
  artikel: { id: string; code: string; bezeichnung: string; preisCent: number; fotoKey: string | null; statusAt: string; grund: string | null; notiz: string | null; rueckgabeFoto?: string | null }[];
  oeffnungszeiten: Record<string, { von: string; bis: string } | null>;
}

export interface MieterPlan {
  plan: { spalten: number; zeilen: number; hintergrundKey: string | null; elemente: PlanElement[] };
  regale: { id: string; nummer: string; lage: string; platz: PlanPlatz | null }[];
  meine: string[];
}
export const useMieterPlan = () => useQuery({ queryKey: ["mieter", "plan"], queryFn: () => rufe<MieterPlan>(api.api.mieter.plan.$get()) });
export const useMieterStart = () => useQuery({ queryKey: ["mieter", "start"], queryFn: () => rufe<MieterStart>(api.api.mieter.start.$get()) });
export const useMieterVertrag = (id: string) => useQuery({ queryKey: ["mieter", "vertrag", id], queryFn: () => rufe<MieterVertrag>(api.api.mieter.vertrag[":id"].$get({ param: { id } })), enabled: !!id });
export const useMieterAbrechnungen = () => useQuery({ queryKey: ["mieter", "abrechnungen"], queryFn: () => rufe<MieterAbrechnungen>(api.api.mieter.abrechnungen.$get()) });
export const useRueckgabeKiste = () => useQuery({ queryKey: ["mieter", "rueckgabe-kiste"], queryFn: () => rufe<RueckgabeKisteDaten>(api.api.mieter["rueckgabe-kiste"].$get()) });
export const useMieterProfil = () => useQuery({ queryKey: ["mieter", "profil"], queryFn: () => rufe<MieterProfil>(api.api.mieter.profil.$get()) });

export function useVertragZustimmen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; vertragTextHash: string }) => rufe<{ status: string; zustimmungAt: string }>(api.api.mieter.vertrag[":id"].zustimmen.$post({ param: { id: v.id }, json: { gelesen: true, vertragTextHash: v.vertragTextHash } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["mieter"] }),
  });
}
export function useProfilSpeichern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (d: { telefon: string; adresse: { strasse: string; plz: string; ort: string }; kontoinhaber: string; auszahlungsweg: "ueberweisung" | "bar"; iban?: string }) => rufe(api.api.mieter.profil.$put({ json: d })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["mieter"] }),
  });
}
export function useHinweisGelesen() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => rufe(api.api.mieter.hinweise[":id"].gelesen.$post({ param: { id } })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["mieter", "start"] }) });
}
export const mieterVertragPdfUrl = (id: string) => `/api/mieter/vertrag/${id}/pdf`;

export const TAGE: [string, string][] = [["mo", "Mo"], ["di", "Di"], ["mi", "Mi"], ["do", "Do"], ["fr", "Fr"], ["sa", "Sa"], ["so", "So"]];
/** „Di bis Fr 10–18 Uhr · Sa 10–14 Uhr · So und Mo geschlossen“ */
export function oeffnungszeitenText(z: Record<string, { von: string; bis: string } | null> | null | undefined): string[] {
  if (!z) return [];
  const zeilen: string[] = [];
  const gruppen: { tage: string[]; text: string }[] = [];
  for (const [k, name] of TAGE) {
    const t = z[k];
    const text = t ? `${t.von.replace(/^0/, "")}–${t.bis.replace(/^0/, "")} Uhr` : "geschlossen";
    const letzte = gruppen[gruppen.length - 1];
    if (letzte && letzte.text === text) letzte.tage.push(name);
    else gruppen.push({ tage: [name], text });
  }
  for (const g of gruppen) zeilen.push(`${g.tage.length > 2 ? `${g.tage[0]} bis ${g.tage[g.tage.length - 1]}` : g.tage.join(" und ")} ${g.text}`);
  return zeilen;
}
